import { APP_BASE_HREF, LocationStrategy, PathLocationStrategy } from '@angular/common';
import { HttpClient, HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { LOCALE_ID, importProvidersFrom } from '@angular/core';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { DateAdapter, MAT_DATE_LOCALE, MAT_DATE_FORMATS } from '@angular/material/core';
import { NativeDateAdapter } from '@angular/material/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, withInMemoryScrolling, withRouterConfig } from '@angular/router';
import { NgIdleKeepaliveModule, Keepalive } from '@ng-idle/keepalive';
import { Idle } from '@ng-idle/core';
import { StoreModule } from '@ngrx/store';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { provideNgxWebstorage, withLocalStorage, withSessionStorage, withNgxWebstorageConfig } from 'ngx-webstorage';
import { NgxMaskModule } from 'ngx-mask';
import { routes } from './app.routes';
import { CacheInterceptor } from './common/interceptors/cache.interceptor';
import { DedupeSlashInterceptor } from './common/interceptors/dedupe-slash.interceptor';
import { portalUserReducer } from './common/components/ecorr-store/portal-user/portal-user.reducer';
import { IdleTimeoutDialogService, AddCommentDialogHttpService, AckDialogService, ApproveCommentDialogService, AddCommentDialogService, CancelConfirmDialogService } from './support/dialogs/services';
import { CaseListService, SubmitCaseService, CaseDetailService } from './support/states/services';
import { HeaderFaqDialogService } from './landing/services/header-faq-dialog.service';
import { HelpDialogService } from './landing/services/help-dialog.service';
import { LanguageService } from './common/services/language.service';
import { AppSettingsService } from './common/services/app-settings.service';
import { AuthcodeHttpService } from './landing/services/authcode-http.service';
import { WINDOW } from './window';

// Factory function required during AOT compilation
export function httpTranslateLoaderFactory(http: HttpClient) {
    return new TranslateHttpLoader(http);
}

export const appConfig = {
    providers: [
        // HTTP Client
        provideHttpClient(withInterceptorsFromDi()),
        
        // Router
        provideRouter(
            routes,
            withInMemoryScrolling({
                scrollPositionRestoration: 'top'
            }),
            withRouterConfig({
                onSameUrlNavigation: 'reload'
            })
        ),
        
        // Animations
        provideAnimations(),

        provideNgxWebstorage(
        withNgxWebstorageConfig({ separator: ':', caseSensitive: true }),
        withLocalStorage(),
        withSessionStorage()
        ),
        
        // Providers
        { provide: APP_BASE_HREF, useValue: '.' },
        { provide: LocationStrategy, useClass: PathLocationStrategy },
        { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { appearance: 'outline' } },
        { provide: HTTP_INTERCEPTORS, useClass: CacheInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: DedupeSlashInterceptor, multi: true }, //order matters, place this after CacheInterceptor 
        { provide: LOCALE_ID, useValue: 'en' },
        { provide: WINDOW, useFactory: () => window },
        { provide: DateAdapter, useClass: NativeDateAdapter },
        { provide: MAT_DATE_LOCALE, useValue: 'en-US' },
        {
            provide: MAT_DATE_FORMATS,
            useValue: {
                parse: {
                    dateInput: 'MM/DD/YYYY',
                },
                display: {
                    dateInput: 'MM/DD/YYYY',
                    monthYearLabel: 'MMM YYYY',
                    dateA11yLabel: 'LL',
                    monthYearA11yLabel: 'MMMM YYYY',
                },
            },
        },
        
        // Services
        MatDialog,
        Idle,
        Keepalive,
        IdleTimeoutDialogService,
        HeaderFaqDialogService,
        HelpDialogService,
        AddCommentDialogHttpService,
        AckDialogService,
        ApproveCommentDialogService,
        AddCommentDialogService,
        CancelConfirmDialogService,
        CaseListService,
        SubmitCaseService,
        CaseDetailService,
        LanguageService,
        AuthcodeHttpService,
        
        // Modules
        importProvidersFrom(
            NgxMaskModule.forRoot({ validation: true }),
            StoreModule.forRoot({ portalUserState: portalUserReducer }),
            NgIdleKeepaliveModule.forRoot(),
            MatDialogModule,
            TranslateModule.forRoot({
                loader: {
                    provide: TranslateLoader,
                    useFactory: httpTranslateLoaderFactory,
                    deps: [HttpClient]
                }
            })
        )
    ]
};
